import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { useLoading } from "@/stores/loading";
import { errorHandler } from "@/utils/errorHandler";
import { useSession } from "@/stores/session";

const apiClient: AxiosInstance = axios.create({
  baseURL:
    process.env.VUE_APP_BASE_URL_API || "https://api.succesmanagement.nl/api",
  withCredentials: true, // Ensures cookies are sent with requests
  timeout: 300000,
});

// Getting access to the loading store functions
const { startLoading, stopLoading } = useLoading();

// Use an interceptor to inject the token into headers of all outgoing requests
// Add a request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config._ignoreLoading) {
      startLoading(); // Start loading before the request is sent
    }
    return config;
  },
  (error: AxiosError) => {
    if (!(error.config as InternalAxiosRequestConfig)?._ignoreLoading) {
      stopLoading(); // Stop loading on request error
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (!(response.config as InternalAxiosRequestConfig)?._ignoreLoading) {
      stopLoading();
    }
    return response;
  },
  async (error: AxiosError) => {
    const session = useSession();

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (originalRequest.url === "/auth/refresh") {
      session.unAuthorize();
      return Promise.reject(error);
    }

    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      !originalRequest._retry // ✅ prevent retry loop
    ) {
      originalRequest._retry = true;

      try {
        await apiClient.post(`/auth/refresh`);
        return apiClient.request(originalRequest);
      } catch (refreshError) {
        session.unAuthorize();
      }
    }

    if (!(error.config as InternalAxiosRequestConfig)?._ignoreLoading) {
      stopLoading();
    }
    errorHandler(error);
    return Promise.reject(error);
  }
);

export default apiClient;
